import React, { useState } from 'react';
import './BuildGraph.css';

/**
 * Modal for confirming graph reprocessing action.
 * User must type "REPROCESS" and check the confirmation checkbox to proceed.
 */
const ReprocessConfirmModal = ({ 
  isOpen, 
  onClose, 
  onConfirm,
  loading = false,
  graphName = 'this graph'
}) => {
  const [confirmationText, setConfirmationText] = useState('');
  const [isChecked, setIsChecked] = useState(false);

  const canConfirm = confirmationText === 'REPROCESS' && isChecked && !loading;

  const handleConfirm = () => {
    if (canConfirm) {
      onConfirm('REPROCESS');
    }
  };

  const handleClose = () => {
    setConfirmationText('');
    setIsChecked(false);
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && canConfirm) {
      handleConfirm();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay reprocess-modal-overlay" onClick={handleClose}>
      <div className="modal-content reprocess-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Reprocess Graph</h3>
          <button 
            className="close-btn" 
            onClick={handleClose}
            disabled={loading}
          >
            &times;
          </button>
        </div>
        
        <div className="modal-body reprocess-modal-body">
          <div className="reprocess-warning">
            <div className="warning-icon">&#9888;</div>
            <div className="warning-content">
              <h4>Warning: This action will delete graph data</h4>
              <p>
                Reprocessing <strong>{graphName}</strong> will permanently delete the following:
              </p>
            </div>
          </div>

          <div className="reprocess-details">
            <div className="delete-list">
              <h5>Will be DELETED:</h5>
              <ul>
                <li>All nodes and their settings</li>
                <li>All synapses (connections)</li>
                <li>All graph-level Q/A pairs</li>
                <li>Processing history and jobs</li>
                <li>Pinecone vector embeddings</li>
                <li>Compiled graph files</li>
              </ul>
            </div>

            <div className="preserve-list">
              <h5>Will be PRESERVED:</h5>
              <ul>
                <li>Document chunks (reset to pending)</li>
                <li>Chunk-level Q/A pairs</li>
                <li>Uploaded documents</li>
                <li>Graph settings and permissions</li>
              </ul>
            </div>
          </div>

          <p className="reprocess-description">
            After reprocessing, you can consolidate the graph to rebuild it from the existing 
            (and potentially edited) chunks.
          </p>

          <div className="confirmation-section">
            <label className="checkbox-container">
              <input
                type="checkbox"
                checked={isChecked}
                onChange={(e) => setIsChecked(e.target.checked)}
                disabled={loading}
              />
              <span className="checkmark"></span>
              I understand this will delete all graph nodes, synapses, and Q/A data
            </label>

            <div className="confirmation-input-group">
              <label htmlFor="reprocess-confirm">
                Type <strong>REPROCESS</strong> to confirm:
              </label>
              <input
                id="reprocess-confirm"
                type="text"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value.toUpperCase())}
                onKeyDown={handleKeyDown}
                placeholder="REPROCESS"
                disabled={loading}
                autoComplete="off"
                autoFocus
              />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button 
            className="btn-secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            className={`btn-danger ${canConfirm ? '' : 'disabled'}`}
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            {loading ? 'Reprocessing...' : 'Reprocess Graph'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReprocessConfirmModal;
